// =====================================================
// PAYMENT REVERSAL MODAL
// Stage 3.2: Payment Processing
// Modal for reversing participant payments
// =====================================================

import { formatBs } from '@/lib/money';
import { useState, useEffect } from 'react';
import { XCircle, Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';

// ===================
// TYPES
// ===================

interface PaymentReversalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  participantName: string;
  paymentAmount: number;
  onSubmit: (reason: string) => Promise<void>;
  isProcessing?: boolean;
}

// ===================
// COMPONENT
// ===================

export function PaymentReversalModal({
  open,
  onOpenChange,
  participantName,
  paymentAmount,
  onSubmit,
  isProcessing = false,
}: PaymentReversalModalProps) {
  const { toast } = useToast();
  const [reversalReason, setReversalReason] = useState<string>('');

  useEffect(() => {
    if (open) setReversalReason('');
  }, [open]);

  const handleSubmit = async () => {
    const reason = reversalReason.trim();
    if (!reason) {
      toast({ title: 'Motivo requerido', description: 'Debe indicar el motivo de la reversión', variant: 'destructive' });
      return;
    }
    try {
      await onSubmit(reason);
    } catch {
      // errors handled by the mutation in PaymentButton
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="size-5 text-destructive" />
            Revertir Pago
          </DialogTitle>
          <DialogDescription>
            Esta acción marcará el pago como revertido y lo devolverá a estado pendiente
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>
              Esta acción no se puede deshacer. El participante volverá a estado PENDIENTE.
            </AlertDescription>
          </Alert>

          <div className="rounded-md bg-muted p-3">
            <p className="text-sm font-medium">{participantName}</p>
            <p className="text-xs text-muted-foreground">
              Monto pagado: {formatBs(paymentAmount)}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reversal-reason">
              Motivo de la reversión <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reversal-reason"
              value={reversalReason}
              onChange={(e) => setReversalReason(e.target.value)}
              placeholder="Ejemplo: Error en el registro, pago duplicado, solicitud del participante..."
              rows={3}
              disabled={isProcessing}
            />
            <p className="text-xs text-muted-foreground">
              Este motivo quedará registrado en el historial del participante
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={isProcessing}>
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Procesando...
              </>
            ) : (
              'Revertir Pago'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
